import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Stepper,
  Step,
  StepButton,
  Button,
  Container,
  Snackbar,
  Typography,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { Send as SendIcon, ArrowBack as ArrowBackIcon } from "@material-ui/icons";

import AssetUploaderManager from "../AssetUploader/UploadStep/AssetUploaderManager";
import AssetNaming from "../AssetUploader/NamingStep/AssetNaming";
import AssetReview from "../AssetUploader/ReviewStep/AssetReview";
import Confirmation from "../Shared/Confirmation/Confirmation";
import BackupStep from "../AssetUploader/BackupStep/BackupStep";
import CompanionStep from "../AssetUploader/CompanionStep/CompanionStep";


import { useSelector, useDispatch } from "react-redux";
import {
  clearSelectedCreatives,
  setCreativeApiError
} from "../../actions/app/creatives";

import { insertCreativesNoAssignments } from "../../services/dcm/creatives";
import { exportToExcel } from "../../utils/dataUtils";
import {
  useUserAdvertisers,
  ADVERTISER_STATUS
} from "../../hooks/useUserAdvertisers";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%"
  },
  backButton: {
    marginRight: theme.spacing(1)
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  stepper: {
    margin: "20px auto 30px",
    width: "50%",
    paddingTop: 0
  }
}));

const next = {
  float: "right"
};

function shouldUploadBackup(creatives) {
  for (let creative of creatives) {
    if (creative.creativeRole === "BACKUP") continue;
    if (creative.file && creative.file.type) {
      if (creative.file.type.includes("zip")) return true;
    }
  }

  return false;
}

function hasAudioCreatives(creatives) {
  for (let creative of creatives) {
    if (creative.file && creative.file.type) {
      if (creative.file.type.startsWith("audio/")) return true;
    }
  }

  return false;
}

function hasVideoCreatives(creatives) {
  for (let creative of creatives) {
    if (creative.file && creative.file.type) {
      if (creative.file.type.startsWith("video/")) return true;
    }
  }

  return false;
}

function getSteps(stepOptions) {
  let steps = ["Upload creative(s)"];

  if (stepOptions.isH5) {
    steps.push("Upload backup creative(s)");
  }

  if (stepOptions.wantsCompanion) {
    steps.push("Add companion creative(s)");
  }

  steps.push("Generate name(s)");
  steps.push("Review");

  return steps;
}

const creativeTypes = ["DISPLAY"];

// Friendly names for the per-type submission outcomes reported on the
// Confirmation screen.
const TYPE_LABELS = {
  INSTREAM_VIDEO: "Video",
  INSTREAM_AUDIO: "Audio",
  DISPLAY: "Display"
};

function getStepContent(stepIndex, props) {
  // Build the step content array dynamically to match getSteps()
  let stepContents = [];

  // Step: Upload
  stepContents.push(
    <div>
      <AssetUploaderManager
        isThumbnailEnabled={true}
        filetype={"image/*, .zip, video/*, audio/*"}
        creativeTypes={creativeTypes}
        selectedCreatives={props.selectedCreatives}
        setSelectedCreatives={props.setSelectedCreatives}
        placements={props.placements}
        canChooseExisting={false}
        ref={(r) => (props.stepRef.current = r)}
        headerExtra={props.hasMedia ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={props.wantsCompanion}
                onChange={(e) => props.setWantsCompanion(e.target.checked)}
                color="primary"
              />
            }
            label="Add companion creatives for media files (image/HTML5 displayed alongside the media player)"
            style={{ marginBottom: 8 }}
          />
        ) : null}
      />
    </div>
  );

  // Step: Backup (only if H5)
  if (props.isH5) {
    stepContents.push(
      <div>
        <BackupStep
          isClickEnabled={true}
          isDragDisabled={true}
          isThumbnailEnabled={false}
          ref={(r) => (props.stepRef.current = r)}
        />
      </div>
    );
  }

  // Step: Companion (only if user opted in)
  if (props.wantsCompanion) {
    stepContents.push(
      <div>
        <CompanionStep
          isClickEnabled={true}
          isDragDisabled={true}
          selectedCreatives={props.selectedCreatives}
          setSelectedCreatives={props.setSelectedCreatives}
          isThumbnailEnabled={false}
          ref={(r) => (props.stepRef.current = r)}
        />
      </div>
    );
  }

  // Step: Naming
  stepContents.push(
    <div>
      <AssetNaming
        isClickEnabled={true}
        isDragDisabled={true}
        isThumbnailEnabled={true}
        advertiserId={props.advertiserId}
        ref={(r) => (props.stepRef.current = r)}
      />
    </div>
  );

  // Step: Review
  stepContents.push(
    <div>
      <AssetReview
        isClickEnabled={false}
        isDragDisabled={true}
        isThumbnailEnabled={true}
        hasCampaignData={false}
        ref={(r) => (props.stepRef.current = r)}
      />
    </div>
  );

  return stepContents[stepIndex] || " ";
}

export default () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  // Tracks an actual submission. Deriving "done" from
  // activeStep === steps.length is unsafe when the step count is derived
  // from creative state: if a step disappears while the user sits on a
  // later one, activeStep can land on steps.length and report a
  // successful submission that never happened.
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [completed, setCompleted] = useState(new Set());
  const [skipped, setSkipped] = useState(new Set());
  const [selectedCreatives, setSelectedCreatives] = useState([]);
  const [snackbarSeverity, setSnackbarSeverity] = useState('warning')
  const [wantsCompanion, setWantsCompanion] = useState(false);
  const {
    advertisers: userAdvertisers,
    status: advertiserStatus,
    message: advertiserMessage,
    retry: retryUserAdvertisers
  } = useUserAdvertisers();
  let [selectedAdvertiserId, setSelectedAdvertiserId] = useState("ALL");
  let [traffickedCreatives, setTraffickedCreatives] = useState({});

  const creativesState = useSelector((state) => state.creatives);
  let isH5 = shouldUploadBackup(Object.values(creativesState.byId));
  let hasAudio = hasAudioCreatives(Object.values(creativesState.byId));
  let hasVideo = hasVideoCreatives(Object.values(creativesState.byId));
  let hasMedia = hasAudio || hasVideo;
  const steps = getSteps({ isH5, wantsCompanion });

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const stepRef = useRef();

  // A failed or empty advertiser check used to be a console.log and a
  // 3-second snackbar, leaving the Next button greyed out with nothing on
  // screen. Keep it visible and recoverable without a re-login.
  const advertiserCheckAlert =
    advertiserStatus === ADVERTISER_STATUS.ERROR ||
    advertiserStatus === ADVERTISER_STATUS.EMPTY ? (
      <MuiAlert
        severity={
          advertiserStatus === ADVERTISER_STATUS.ERROR ? "error" : "warning"
        }
        variant="outlined"
        style={{ marginBottom: 16 }}
        action={
          <Button color="inherit" size="small" onClick={retryUserAdvertisers}>
            Retry
          </Button>
        }
      >
        {advertiserMessage}
      </MuiAlert>
    ) : null;

  const handleNext = () => {
    if (
      stepRef &&
      stepRef.current &&
      stepRef.current.hasOwnProperty("validate")
    ) {
      const validation = stepRef.current.validate();

      if (validation.hasErrors) {
        if (validation.message) {
          setSnackbarMessage(validation.message);
          setSnackbarOpen(true);
        }

        return;
      }
    }

    dispatch(clearSelectedCreatives());
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setHasSubmitted(false);
    setActiveStep(0);
  };

  const handleStep = (step) => () => {
    // The Stepper header stays mounted on the Confirmation screen, so a
    // StepButton click has to drop out of it — otherwise activeStep moves
    // but Confirmation keeps rendering and the user is stuck there.
    setHasSubmitted(false);
    setSelectedCreatives([]);
    setActiveStep(step);
    dispatch(clearSelectedCreatives());
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  function isStepComplete(step) {
    return completed.has(step);
  }

  const handleTraffic = async () => {
    let allCreatives = Object.values(creativesState.byId);
    // userAdvertisers is [{id, name}]. If a specific advertiser is selected,
    // narrow the list to just that one; otherwise send all the user's
    // advertisers (the original behavior).
    let advertiserIds =
      selectedAdvertiserId === "ALL"
        ? userAdvertisers.map((a) => (a && a.id ? a.id : a))
        : [selectedAdvertiserId];
    let opts = { advertiserIds };

    // Partition creatives by type to avoid sending mixed types with wrong compatibility
    let videoCreatives = allCreatives.filter(c => c.file && c.file.type && c.file.type.startsWith("video/"));
    let audioCreatives = allCreatives.filter(c => c.file && c.file.type && c.file.type.startsWith("audio/"));
    let displayCreatives = allCreatives.filter(c =>
      !c.file || !c.file.type ||
      (!c.file.type.startsWith("video/") && !c.file.type.startsWith("audio/"))
    );

    // Include companion/backup creatives with their parent's batch
    let getLinkedCreatives = (primaries, allCreatives) => {
      let linkedIds = new Set();
      primaries.forEach(c => {
        if (c.companionId) linkedIds.add(c.companionId);
        if (c.backupId) linkedIds.add(c.backupId);
      });
      let linked = allCreatives.filter(c => linkedIds.has(c.id));
      return [...primaries, ...linked];
    };

    let batches = [];
    if (videoCreatives.length) {
      batches.push([getLinkedCreatives(videoCreatives, allCreatives), "INSTREAM_VIDEO"]);
    }
    if (audioCreatives.length) {
      batches.push([getLinkedCreatives(audioCreatives, allCreatives), "INSTREAM_AUDIO"]);
    }
    if (displayCreatives.length) {
      batches.push([displayCreatives, "DISPLAY"]);
    }

    // Clear any prior error so the Confirmation screen reflects only this
    // attempt.
    dispatch(setCreativeApiError(new Error(), false));

    // Await the batches and write ONE workbook covering all of them. Each call
    // used to write its own Bart export, and three concurrent downloads of the
    // same filename made the browser silently drop some (QA got 2 exports for
    // a video + audio + display batch).
    let results = await Promise.all(
      batches.map(([batchCreatives, type]) =>
        insertCreativesNoAssignments(batchCreatives, opts, type)
          .then((r) => ({ type, result: r }))
          // insertCreativesNoAssignments catches its own failures and returns
          // them, but guard anyway so one unexpected throw can't reject the
          // whole Promise.all and lose the other batches' results.
          .catch((e) => ({ type, result: { exportNames: [], error: e } }))
      )
    );

    let succeeded = results.filter((r) => r.result && !r.result.error);
    let failed = results.filter((r) => !r.result || r.result.error);

    let exportNames = succeeded.reduce(
      (acc, r) => acc.concat(r.result.exportNames || []),
      []
    );

    if (exportNames.length) {
      exportToExcel("Bart_Creatives_Export", exportNames);
    }

    // Report per-type outcomes instead of letting one failed batch surface as a
    // single unattributed error. Confirmation splits the message on newlines,
    // so each line renders separately.
    if (failed.length) {
      let lines = failed.map((r) => {
        let e = r.result && r.result.error;
        let detail = e && e.message ? e.message : "submission failed";
        return `${TYPE_LABELS[r.type] || r.type}: ${detail}`;
      });

      if (succeeded.length) {
        lines.unshift(
          `Submitted successfully: ${succeeded
            .map((r) => TYPE_LABELS[r.type] || r.type)
            .join(", ")}. The following did not:`
        );
      }

      dispatch(setCreativeApiError(new Error(lines.join("\n")), true));
    }

    // Set before advancing: the Confirmation screen is gated on this,
    // not on the step index.
    setHasSubmitted(true);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  return (
    <div className={classes.root}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 8px",
          minHeight: 48
        }}
      >
        <Link to="/" style={{ color: "#000", textDecoration: "none" }}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <ArrowBackIcon style={{ marginRight: 8 }} fontSize="small" />
            <Typography variant="body2">
              Return to creative assignment tool
            </Typography>
          </div>
        </Link>
        {userAdvertisers.length > 1 ? (
          <FormControl variant="outlined" size="small" style={{ minWidth: 280 }}>
            <InputLabel id="preloader-advertiser-label">Advertiser</InputLabel>
            <Select
              labelId="preloader-advertiser-label"
              label="Advertiser"
              value={selectedAdvertiserId}
              onChange={(e) => setSelectedAdvertiserId(e.target.value)}
            >
              <MenuItem value="ALL">All advertisers ({userAdvertisers.length})</MenuItem>
              {[...userAdvertisers]
                .sort((a, b) =>
                  String(a.name || a).localeCompare(String(b.name || b), undefined, { sensitivity: "base" })
                )
                .map((a) => (
                  <MenuItem key={a.id || a} value={a.id || a}>
                    {a.name ? `${a.name} (${a.id})` : a}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        ) : (
          <span />
        )}
      </div>
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        nonLinear
        className={classes.stepper}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          const buttonProps = {};
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepButton
                onClick={handleStep(index)}
                completed={isStepComplete(index)}
                {...buttonProps}
              >
                {label}
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
      <Container maxWidth="lg">
        <div>
          {hasSubmitted ? (
            <Confirmation
              handleResetParent={handleReset}
              isMultiAdvertiser={true}
            />
          ) : (
            <div>
              <div style={next}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={classes.button}
                  disableElevation
                >
                  Back
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTraffic}
                    startIcon={<SendIcon />}
                    disableElevation
                  >
                    Submit Creatives
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disableElevation
                    disabled={
                      advertiserStatus === ADVERTISER_STATUS.LOADING ||
                      advertiserStatus === ADVERTISER_STATUS.EMPTY
                    }
                  >
                    Next
                  </Button>
                )}
              </div>
              {advertiserCheckAlert}
              {getStepContent(activeStep, {
                stepRef,
                selectedCreatives,
                setSelectedCreatives,
                placements: creativesState.assignedPlacements,
                isH5,
                hasAudio,
                hasVideo,
                hasMedia,
                wantsCompanion,
                setWantsCompanion,
                setActiveStep,
                // Scope the naming step's URL list to the chosen advertiser.
                // null for "ALL" so it falls back to every user advertiser.
                advertiserId:
                  selectedAdvertiserId === "ALL" ? null : selectedAdvertiserId
              })}
            </div>
          )}
        </div>
      </Container>
      <Snackbar
        // autoHideDuration={3000}
        open={snackbarOpen}
        onClose={() => {setSnackbarOpen(false); setSnackbarSeverity('warning')}}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};
